import React, { useState, useEffect, useRef } from 'react';
import { Modal, View, StyleSheet } from 'react-native';
import Text from './../Text';
import Button from './../Button';

interface WarningModalProps {
    visible: boolean;
    onContinue: () => void;
    onTimeout: () => void;
    warningPeriodSeconds: number;
}

// Custom hook for modal countdown
const useModalCountdown = (visible: boolean, onTimeout: () => void, warningPeriodSeconds: number) => {
    const [countdown, setCountdown] = useState(warningPeriodSeconds);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const onTimeoutRef = useRef(onTimeout);

    // Keep the callback ref up to date
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    useEffect(() => {
        if (visible) {
            // Clear any existing timer first
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }

            let currentCount = warningPeriodSeconds;
            setCountdown(currentCount);

            timerRef.current = setInterval(() => {
                currentCount = currentCount - 1;
                setCountdown(currentCount);

                if (currentCount <= 0) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                    // Call timeout after a small delay to ensure UI shows 0
                    setTimeout(() => {
                        onTimeoutRef.current();
                    }, 100);
                }
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setCountdown(warningPeriodSeconds);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [visible, warningPeriodSeconds]); // Added warningPeriodSeconds dependency

    // Format countdown as HH:MM:SS or MM:SS, omitting leading zeros
    const formatTime = (seconds: number): string => {
        const totalSeconds = Math.max(0, seconds);
        const hours = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        // If more than 59 minutes, show hours (omit leading zero for hours)
        if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }

        // For MM:SS, omit leading zero for minutes if less than 10
        const formattedMins = mins < 10 ? mins.toString() : mins.toString().padStart(2, '0');
        return `${formattedMins}:${secs.toString().padStart(2, '0')}`;
    };

    return { countdown, formattedTime: formatTime(countdown) };
};

// Warning Modal Component
const WarningModal: React.FC<WarningModalProps> = ({ visible, onContinue, onTimeout, warningPeriodSeconds }) => {
    const { formattedTime } = useModalCountdown(visible, onTimeout, warningPeriodSeconds);

    if (!visible) return null;

    return (
        <Modal visible={true} transparent animationType="fade">
            <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                    <Text variant="bold-caption-1" style={styles.modalTitle}>
                        Session Logout In
                    </Text>
                    <Text variant="h1" style={styles.timerText}>
                        {formattedTime}
                    </Text>
                    <Text variant="caption-1" style={styles.modalSubtext}>
                        Would you like to continue?
                    </Text>
                    <Button icon="ChevronRight2" variant="primary" onPress={onContinue} title="Continue"></Button>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 30,
        margin: 15,
        minWidth: 320,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center',
    },
    timerText: {
        fontSize: 48,
        fontWeight: 'bold',
        color: '#000000',
        marginVertical: 20,
        fontFamily: 'monospace',
        textAlign: 'center',
    },
    modalSubtext: {
        fontSize: 14,
        marginBottom: 20,
        textAlign: 'center',
    },
    continueButton: {
        backgroundColor: '#007AFF',
        paddingHorizontal: 30,
        paddingVertical: 12,
        borderRadius: 8,
    },
    continueButtonText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '600',
    },
});

export default WarningModal;
